import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { useAuth } from '@/hooks/useAuth';
import api from '@/lib/api';
import { formatTime12Hour, calculateHours } from '@/utils/calendarHelpers';
import { FAMILY_ROLE_HOURLY_RATE } from '@/utils/constants';

interface EarningsSummaryProps {
  userId: string;
  userRole: string;
  hourlyWage?: number;
  viewingOther?: boolean;
  compact?: boolean;
}

export function EarningsSummary({ userId, userRole, hourlyWage: propWage, viewingOther, compact }: EarningsSummaryProps) {
  const { user } = useAuth();
  const { data: slots = [], isLoading } = useQuery({
    queryKey: ['my-slots', userId],
    queryFn: async () => {
      const params = new URLSearchParams({ caretaker_id: userId });
      const res = await api.get(`/timeslots?${params}`);
      return res.data.slots || [];
    },
    enabled: !!userId,
    staleTime: 1000 * 5,
    refetchInterval: 5000,
  });

  const now = new Date();

  // Separate past and future slots based on slot end time
  const pastSlots = slots.filter((slot: any) => {
    const slotEnd = new Date(`${slot.date}T${slot.end_time}:00`);
    return slotEnd < now || slot.status === 'completed';
  }).sort((a: any, b: any) => new Date(b.date).getTime() - new Date(a.date).getTime());

  const futureSlots = slots.filter((slot: any) => {
    const slotEnd = new Date(`${slot.date}T${slot.end_time}:00`);
    return slotEnd >= now && slot.status !== 'completed' && slot.status !== 'cancelled';
  }).sort((a: any, b: any) => new Date(a.date).getTime() - new Date(b.date).getTime());

  // Calculate totals — prefer actual hours/pay when available
  const totalHours = pastSlots.reduce((acc: number, slot: any) => acc + (slot.actual_hours_worked ?? slot.hours_worked ?? 0), 0);
  const totalEarnings = pastSlots.reduce((acc: number, slot: any) => acc + (slot.actual_pay_amount ?? slot.pay_amount ?? 0), 0);
  const plannedHours = pastSlots.reduce((acc: number, slot: any) => acc + (slot.hours_worked || 0), 0);
  const plannedEarnings = pastSlots.reduce((acc: number, slot: any) => acc + (slot.pay_amount || 0), 0);
  const hasActualDifference = Math.abs(totalHours - plannedHours) >= 0.1;

  // Calculate projected (future) totals - use flat $20/hour for 'family' role
  const hourlyWage = userRole === 'family' ? FAMILY_ROLE_HOURLY_RATE : (propWage || user?.hourly_wage || 0);
  const projectedHours = futureSlots.reduce((acc: number, slot: any) => {
    return acc + calculateHours(slot.start_time, slot.end_time);
  }, 0);
  const projectedEarnings = projectedHours * hourlyWage;

  if (isLoading) {
    return (
      <div className={compact ? '' : 'bg-card rounded-xl border border-slate-700 p-6'}>
        <div className="text-center text-gray-400">Loading earnings...</div>
      </div>
    );
  }

  return (
    <div className={compact ? '' : 'bg-card rounded-xl border border-slate-700 p-6'}>
      <div className="mb-4">
        <h2 className={`font-semibold text-white ${compact ? 'text-base' : 'text-xl'}`}>{viewingOther ? 'Earnings' : 'Your Earnings'}</h2>
        <p className="text-sm text-gray-400 mt-1">
          Rate: ${hourlyWage.toFixed(2)}/hour
        </p>
      </div>

      {/* Stats */}
      <div className={`grid grid-cols-1 sm:grid-cols-2 ${compact ? 'gap-2 mb-4' : 'md:grid-cols-4 gap-4 mb-6'}`}>
        <div className={`bg-slate-800 rounded-lg ${compact ? 'p-2.5' : 'p-4'}`}>
          <div className={`font-bold text-white ${compact ? 'text-lg' : 'text-2xl'}`}>{totalHours.toFixed(1)}</div>
          <div className={`text-gray-400 ${compact ? 'text-xs' : 'text-sm'}`}>Hours Worked</div>
          {hasActualDifference && (
            <div className="text-xs text-gray-500 mt-0.5">{plannedHours.toFixed(1)} planned</div>
          )}
        </div>
        <div className={`bg-slate-800 rounded-lg ${compact ? 'p-2.5' : 'p-4'}`}>
          <div className={`font-bold text-emerald-400 ${compact ? 'text-lg' : 'text-2xl'}`}>${totalEarnings.toFixed(2)}</div>
          <div className={`text-gray-400 ${compact ? 'text-xs' : 'text-sm'}`}>Earned</div>
          {hasActualDifference && (
            <div className="text-xs text-gray-500 mt-0.5">${plannedEarnings.toFixed(2)} planned</div>
          )}
        </div>
        <div className={`bg-slate-800 rounded-lg ${compact ? 'p-2.5' : 'p-4'}`}>
          <div className={`font-bold text-blue-400 ${compact ? 'text-lg' : 'text-2xl'}`}>{projectedHours.toFixed(1)}</div>
          <div className={`text-gray-400 ${compact ? 'text-xs' : 'text-sm'}`}>Upcoming Hours</div>
        </div>
        <div className={`bg-slate-800 rounded-lg ${compact ? 'p-2.5' : 'p-4'}`}>
          <div className={`font-bold text-blue-400 ${compact ? 'text-lg' : 'text-2xl'}`}>${projectedEarnings.toFixed(2)}</div>
          <div className={`text-gray-400 ${compact ? 'text-xs' : 'text-sm'}`}>Projected</div>
        </div>
      </div>

      {/* Upcoming slots */}
      {futureSlots.length > 0 && (
        <div className="mb-6">
          <h3 className="text-sm font-medium text-gray-400 mb-2">Upcoming Slots ({futureSlots.length})</h3>
          <div className="space-y-2 max-h-48 overflow-y-auto">
            {futureSlots.slice(0, 5).map((slot: any) => {
              const hours = calculateHours(slot.start_time, slot.end_time);
              const earnings = hours * hourlyWage;
              return (
                <div
                  key={slot.id}
                  className="flex items-center justify-between p-3 bg-blue-500/10 border border-blue-500/20 rounded-lg"
                >
                  <div>
                    <div className="text-white font-medium">
                      {format(new Date(slot.date + 'T00:00:00'), 'EEE, MMM d')}
                    </div>
                    <div className="text-sm text-gray-400">
                      {formatTime12Hour(slot.start_time)} - {formatTime12Hour(slot.end_time)}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-blue-400">{hours.toFixed(1)} hrs</div>
                    <div className="text-sm text-blue-400">${earnings.toFixed(2)}</div>
                  </div>
                </div>
              );
            })}
            {futureSlots.length > 5 && (
              <div className="text-center text-sm text-gray-400 pt-2">
                +{futureSlots.length - 5} more upcoming
              </div>
            )}
          </div>
        </div>
      )}

      {/* Past slots list */}
      {pastSlots.length > 0 && (
        <div className="space-y-2 max-h-48 overflow-y-auto">
          <h3 className="text-sm font-medium text-gray-400 mb-2">Completed Sessions</h3>
          {pastSlots.slice(0, 5).map((slot: any) => {
            const displayHours = slot.actual_hours_worked ?? slot.hours_worked;
            const displayPay = slot.actual_pay_amount ?? slot.pay_amount;
            const hasActual = slot.actual_start_time && slot.actual_end_time;
            return (
              <div
                key={slot.id}
                className="flex items-center justify-between p-3 bg-slate-800 rounded-lg"
              >
                <div>
                  <div className="text-white font-medium">
                    {format(new Date(slot.date + 'T00:00:00'), 'EEE, MMM d')}
                  </div>
                  <div className="text-sm text-gray-400">
                    {hasActual
                      ? `${formatTime12Hour(slot.actual_start_time)} - ${formatTime12Hour(slot.actual_end_time)}`
                      : `${formatTime12Hour(slot.start_time)} - ${formatTime12Hour(slot.end_time)}`}
                  </div>
                  {slot.no_show ? (
                    <div className="text-xs text-red-400">No Show</div>
                  ) : null}
                </div>
                <div className="text-right">
                  <div className="text-white">{displayHours?.toFixed(1) || '?'} hrs</div>
                  <div className="text-sm text-emerald-400">${displayPay?.toFixed(2) || '0.00'}</div>
                </div>
              </div>
            );
          })}
          {pastSlots.length > 5 && (
            <div className="text-center text-sm text-gray-400 pt-2">
              +{pastSlots.length - 5} more completed
            </div>
          )}
        </div>
      )}

      {pastSlots.length === 0 && futureSlots.length === 0 && (
        <div className="text-center py-6 text-gray-400">
          No slots yet. Claim some time slots to get started!
        </div>
      )}
    </div>
  );
}
